/**
 * TranslatableText Component - Auto-translates content based on UI language
 * 
 * Wraps text content and automatically translates it when the UI language
 * differs from the detected content language.
 */
import React from 'react';
import { useTranslatedContent } from '@/hooks/useContentTranslation';
import { Loader2, Languages } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useLanguage } from '@/lib/context';

/**
 * TranslatableText - Auto-translating text wrapper
 * 
 * @param {string} children - The original text content
 * @param {string} originalLang - Optional: known original language
 * @param {string} className - Additional CSS classes
 * @param {boolean} showIndicator - Show translation indicator icon
 * @param {string} as - HTML element type (default: 'span')
 */
export const TranslatableText = ({ 
  children, 
  originalLang,
  className,
  showIndicator = false,
  as: Component = 'span',
  ...props 
}) => {
  const { text, isTranslated, isLoading } = useTranslatedContent(children, originalLang);
  const { t } = useLanguage();
  
  if (!children) return null;
  
  return (
    <Component 
      className={cn(className, isTranslated && 'translated-content')}
      data-translated={isTranslated}
      data-original-lang={originalLang}
      {...props}
    >
      {text}
      {isLoading && (
        <Loader2 className="inline-block ml-1 h-3 w-3 animate-spin text-muted-foreground" />
      )}
      {showIndicator && isTranslated && !isLoading && (
        <Languages className="inline-block ml-1 h-3 w-3 text-blue-500" title={t('common.translated')} />
      )}
    </Component>
  );
};

/**
 * TranslatableCard - Card with translatable title and description
 * 
 * @param {string} title - The title text
 * @param {string} description - The description text  
 * @param {string} originalLang - Optional: known original language
 * @param {React.ReactNode} children - Additional card content
 */
export const TranslatableCard = ({
  title,
  description,
  originalLang,
  children,
  className,
  ...props
}) => {
  const { text: translatedTitle, isTranslated: titleTranslated } = useTranslatedContent(title, originalLang);
  const { text: translatedDesc, isTranslated: descTranslated } = useTranslatedContent(description, originalLang);
  const { t } = useLanguage();
  const isTranslated = titleTranslated || descTranslated;
  
  return (
    <div className={cn(className)} {...props}>
      {title && (
        <h3 className="font-semibold">
          {translatedTitle}
          {isTranslated && (
            <Languages className="inline-block ml-2 h-3 w-3 text-blue-500" title={t('common.translated')} />
          )}
        </h3>
      )}
      {description && (
        <p className="text-sm text-muted-foreground mt-1">
          {translatedDesc}
        </p>
      )}
      {children}
    </div>
  );
};

/**
 * ToggleableTranslation - Shows original with option to view translation
 * 
 * @param {string} text - The original text
 * @param {string} originalLang - Optional: known original language
 */
export const ToggleableTranslation = ({ text, originalLang, className }) => {
  const { t } = useLanguage();
  const [showOriginal, setShowOriginal] = React.useState(false);
  const { 
    text: translatedText, 
    isTranslated, 
    isLoading, 
    originalText 
  } = useTranslatedContent(text, originalLang);
  
  if (!isTranslated) {
    return <span className={className}>{text}</span>;
  }
  
  return (
    <div className={cn('relative', className)}>
      <span>{showOriginal ? originalText : translatedText}</span>
      {isLoading && (
        <Loader2 className="inline-block ml-1 h-3 w-3 animate-spin" />
      )}
      <button
        onClick={() => setShowOriginal(!showOriginal)}
        className="ml-2 text-xs text-blue-500 hover:text-blue-700 hover:underline"
      >
        {showOriginal ? t('common.viewTranslation') : t('common.viewOriginal')}
      </button>
    </div>
  );
};

export default TranslatableText;
